import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@circuitry-ai/doc-data';

type FormConfig = Record<string, any> | null;

// ---------------------------------------------------------------------------
// Module-level cache — fetchVehicleAndClaims uses the contract already
// fetched by fetchContractDetails without a second network call.
// ---------------------------------------------------------------------------
let _lastFetchedContract: Record<string, any> | null = null;

const formatDate = (iso?: string) => (iso ? iso.split('T')[0] : '');

// ---------------------------------------------------------------------------
// Normalise API contract response → snake_case keys matching contract_details
// section field IDs exactly (spec §7.1 — "response keys match field ids").
// ---------------------------------------------------------------------------
const normalizeContractDetails = (c: Record<string, any>) => ({
  contract_number: c.contractNumber ?? '',
  status: c.status?.description ?? c.status?.code ?? '',
  contract_holder: [c.customer?.firstName, c.customer?.lastName]
    .filter(Boolean)
    .join(' '),
  coverage_plan: c.coverage?.description ?? '',
  effective_date: formatDate(c.effectiveDate),
  expiration_date: formatDate(c.expirationDate),
  vin: c.vehicle?.vin ?? '',
  dealer_code: c.dealerNumber ?? '',
  deductible: c.coverage?.terms?.[0]?.deductible?.amount ?? 0,
  max_coverage: c.retailCost?.amount ?? 0,
});

// ---------------------------------------------------------------------------
// Normalise API contract response → snake_case keys matching vehicle_coverage
// section field IDs exactly (spec §7.2).
// ---------------------------------------------------------------------------
const normalizeVehicleCoverage = (c: Record<string, any>) => ({
  model_year: String(c.vehicle?.vehicleYear ?? ''),
  make: c.vehicle?.make ?? '',
  model_trim: c.vehicle?.model ?? '',
  current_mileage: c.saleOdometer ? `${c.saleOdometer} mi` : '',
  vin_full: c.vehicle?.vin ?? '',
  coverage_plan: c.coverage?.description ?? '',
  deductible: c.coverage?.terms?.[0]?.deductible?.amount ?? 0,
  effective_date: formatDate(c.effectiveDate),
  expiration_date: formatDate(c.expirationDate),
  max_coverage: c.retailCost?.amount ?? 0,
  dealer_code: c.dealerNumber ?? '',
});

// ---------------------------------------------------------------------------
// Default initial form data (spec §5.1). Used when FormComponent does not
// pass explicit initialData to useClaimsIntakeFormRuntime.
// ---------------------------------------------------------------------------
const DEFAULT_INITIAL_FORM_DATA: Record<string, any> = {
  contract_lookup: { vin: '' },
  contract_details: {},
  vehicle_coverage: {},
  vehicle_details: { arrival_method: '', at_service_center: '' },
  claim_details: { reimbursement_method: '' },
  service_center: {
    service_name: '', contact_name: '', physical_address: '',
    contact_phone: '', billing_address: '', contact_email: '',
    labor_rate_schedule: '', labor_rate: '',
  },
  repairs: [
    {
      complaint: '', cause: '', correction: '',
      labor_lines: [{ labor_description: '', labor_repair_notes: '', labor_amount: '' }],
      part_lines: [{ part_number: '', part_description: '', part_quantity: '', part_unit_price: '', part_amount: '' }],
    },
  ],
};

// ---------------------------------------------------------------------------
// Blank row factories for repeatable groups
// ---------------------------------------------------------------------------
const BLANK_REPAIR_ROW = (): Record<string, any> => ({
  complaint: '', cause: '', correction: '',
  labor_lines: [{ labor_description: '', labor_repair_notes: '', labor_amount: '' }],
  part_lines: [{ part_number: '', part_description: '', part_quantity: '', part_unit_price: '', part_amount: '' }],
});

const BLANK_NESTED_ROWS: Record<string, () => Record<string, string>> = {
  labor_lines: () => ({ labor_description: '', labor_repair_notes: '', labor_amount: '' }),
  part_lines: () => ({ part_number: '', part_description: '', part_quantity: '', part_unit_price: '', part_amount: '' }),
};

// ---------------------------------------------------------------------------
// Pure validateSection implementation (spec §6.3).
// Separated from the hook so validateForm can call it without closing over
// hook state.
// ---------------------------------------------------------------------------
const runValidateSection = (
  sectionKey: string,
  sectionData: Record<string, any> | undefined | null,
  fc: Record<string, any>
): Record<string, string> => {
  const sections = fc?.sections;
  if (!Array.isArray(sections)) return {};
  const section = sections.find(
    (s: Record<string, any>) => s.key === sectionKey
  );
  if (!section) return {};

  const errors: Record<string, string> = {};
  for (const field of section.fields ?? []) {
    // Nested repeatable groups are validated separately per-row (spec §6.3)
    if (field.fieldType === 'repeatable_group') continue;

    const value = sectionData?.[field.id];
    const isEmpty = value === undefined || value === null || value === '';

    if (field.required && isEmpty) {
      errors[field.id] =
        field.validation?.requiredMessage ?? `${field.label} is required.`;
      continue;
    }

    if (field.validation?.pattern && !isEmpty) {
      const regex = new RegExp(field.validation.pattern);
      if (!regex.test(String(value))) {
        errors[field.id] =
          field.validation?.patternMessage ?? `${field.label} is invalid.`;
      }
    }
  }
  return errors;
};

// ---------------------------------------------------------------------------
// Maps flat parser_results from the document parser to form field values.
// Walks every section/field in formConfig, looks up the value by schemaPath
// then by field id, and returns { fieldId: value } for matched fields.
// Mirrors formPrefillFunction in UxGenieFormEngine (kept in sync manually).
// ---------------------------------------------------------------------------
const formPrefillFunction = (
  parserResults: Record<string, any>,
  fc: Record<string, any>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  (fc?.sections ?? []).forEach((section: Record<string, any>) =>
    (section.fields ?? []).forEach((field: Record<string, any>) => {
      const value =
        parserResults?.[field.schemaPath] ?? parserResults?.[field.id];
      if (value !== undefined && value !== null) {
        result[field.id] = value;
      }
    })
  );
  return result;
};

// ---------------------------------------------------------------------------
// Maps the invoice document parser's parser_results into the shape expected
// by the claims form's formData state.
//
// The parser returns domain-specific keys (invoice_generator_name, 3Cs, etc.)
// that don't match the form's field IDs 1:1. This function owns that
// translation so nothing else in the stack needs to know about the parser's
// schema.
//
// Mapping rules:
//   service_center.service_name      ← invoice_generator_name[0]
//   service_center.contact_name      ← invoice_generator_name[0]
//   service_center.physical_address  ← invoice_generator_address[0]
//   (phone / email / billing_address / rates — left blank, not in parser output)
//
//   repairs[]                        ← 3Cs[i] → { complaint, cause, correction }
//     .labor_lines[]                 ← labor_lines[i] wrapped in array (1:1 by index)
//     .part_lines[]                  ← ALL part_lines attached to first repair row
//                                       (parts can't be attributed per-repair from parser)
// ---------------------------------------------------------------------------

interface InvoiceParserResults {
  invoice_generator_name?: string[];
  invoice_generator_address?: string[];
  labor_lines?: Array<{
    labor_description?: string;
    labor_repair_notes?: string;
    labor_amount?: string;
    labor_hours?: string;
    [key: string]: unknown;
  }>;
  part_lines?: Array<{
    part_number?: string;
    part_description?: string;
    part_quantity?: string;
    part_unit_price?: string;
    part_amount?: string;
    [key: string]: unknown;
  }>;
  '3Cs'?: Array<{
    complaint?: string;
    cause?: string;
    correction?: string;
  }>;
  [key: string]: unknown;
}

export const mapInvoiceParserToFormData = (
  parserResults: InvoiceParserResults
): { service_center: Record<string, string>; repairs: Record<string, any>[] } => {
  // ── service_center ───────────────────────────────────────────────────────
  const generatorName = parserResults.invoice_generator_name?.[0] ?? '';
  const generatorAddr = parserResults.invoice_generator_address?.[0] ?? '';

  const service_center: Record<string, string> = {};
  if (generatorName) service_center.service_name = generatorName;
  if (generatorName) service_center.contact_name = generatorName;
  if (generatorAddr) service_center.physical_address = generatorAddr;

  // ── repairs ──────────────────────────────────────────────────────────────
  const threeCsRaw = parserResults['3Cs'] ?? [];
  const laborRaw = parserResults.labor_lines ?? [];
  const partsRaw = parserResults.part_lines ?? [];

  // One repair row per 3C entry.  Fall back to at least one blank row if the
  // parser returned no 3Cs but did return labor / part lines.
  const rowCount = threeCsRaw.length || 1;

  const repairs: Record<string, any>[] = Array.from({ length: rowCount }, (_, i) => {
    const threeC = threeCsRaw[i] ?? {};

    // Map the corresponding labor line for this row (1 labor → 1 labor_lines entry).
    const laborEntry = laborRaw[i];
    const labor_lines = laborEntry
      ? [{
        labor_description: laborEntry.labor_description ?? '',
        labor_repair_notes: laborEntry.labor_repair_notes ?? '',
        labor_amount: laborEntry.labor_amount ?? '',
      }]
      : [{ labor_description: '', labor_repair_notes: '', labor_amount: '' }];

    // Parts are attached to the FIRST repair row only — the parser does not
    // attribute individual parts to specific repairs.
    const part_lines = i === 0
      ? partsRaw.map((p) => ({
        part_number: p.part_number ?? '',
        part_description: p.part_description ?? '',
        part_quantity: p.part_quantity ?? '',
        part_unit_price: p.part_unit_price ?? '',
        part_amount: p.part_amount ?? '',
      }))
      : [{ part_number: '', part_description: '', part_quantity: '', part_unit_price: '', part_amount: '' }];

    return {
      complaint: threeC.complaint ?? '',
      cause: threeC.cause ?? '',
      correction: threeC.correction ?? '',
      labor_lines,
      part_lines,
    };
  });

  return { service_center, repairs };
};

// ---------------------------------------------------------------------------
// Builds the POST payload for the 3Cs enrichment API.
//
// Called whenever complaint, cause, or correction changes — with or without
// a prior file upload. parserResults is the raw output stored after
// handleUploadChange; it is an empty object when no file was uploaded.
//
// The server receives the full repairs array (3Cs + line items) so it has
// complete context for enrichment, plus the original parser output when
// available for higher-quality results.
// ---------------------------------------------------------------------------
export const build3CsUpdatePayload = (
  repairs: Record<string, any>[],
  parserResults: Record<string, unknown>
): Record<string, any> => ({
  repairs: repairs.map((r) => ({
    complaint: r.complaint ?? '',
    cause: r.cause ?? '',
    correction: r.correction ?? '',
    labor_lines: (r.labor_lines ?? []).map((l: Record<string, any>) => ({
      labor_description: l.labor_description ?? '',
      labor_repair_notes: l.labor_repair_notes ?? '',
      labor_amount: l.labor_amount ?? '',
    })),
    part_lines: (r.part_lines ?? []).map((p: Record<string, any>) => ({
      part_number: p.part_number ?? '',
      part_description: p.part_description ?? '',
      part_quantity: p.part_quantity ?? '',
      part_unit_price: p.part_unit_price ?? '',
      part_amount: p.part_amount ?? '',
    })),
  })),
  // Only present when a file was uploaded — gives the server the raw extracted
  // document data to improve enrichment quality.
  ...(Object.keys(parserResults).length > 0 ? { parser_results: parserResults } : {}),
});

// ---------------------------------------------------------------------------
// useClaimsHostFunctions
//
// Returns the complete set of host functions needed by Genie-generated claims
// form components. Pass the returned object as `hostFunctions` to
// GenieRenderingEngine / UxGenieFormEngine.
// ---------------------------------------------------------------------------
export const useClaimsHostFunctions = (_formConfig: FormConfig) => {
  const token = useSelector((state: RootState) => state.auth.token);

  return React.useMemo(() => {
    // -----------------------------------------------------------------------
    // Internal HTTP helper (authenticated)
    // -----------------------------------------------------------------------
    const fetchJson = async (input: RequestInfo | URL, init?: RequestInit) => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers as Record<string, string> | undefined),
      };
      const response = await fetch(input, { ...init, headers });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      return response.json();
    };

    // -----------------------------------------------------------------------
    // Internal utilities — NOT injected into component scope (spec §8.1)
    // -----------------------------------------------------------------------

    // -----------------------------------------------------------------------
    // File upload pipeline — 3 sequential steps per spec:
    //   1. Generate presigned S3 URLs  (POST /api/v1/presignedurl/generate)
    //   2. Upload raw bytes to S3      (PUT presigned_url, no auth)
    //   3. Parse the document          (POST invoicedocparser endpoint)
    // -----------------------------------------------------------------------

    const PRESIGNED_URL_ENDPOINT =
      (import.meta.env.CTY_API_URL as string | undefined) ??
      'https://sandbox.circuitry.ai';

    const DOC_PARSER_ENDPOINT =
      (import.meta.env.CTY_DOC_PARSER_URL as string | undefined) ??
      'https://sandbox.invoicedocparser.circuitry.ai';

    /** Step 1: Get presigned upload URLs for the given files. */
    const generatePresignedUrls = async (
      files: File[]
    ): Promise<Array<{ presigned_url: string; source_id: string; file_name: string }>> => {
      const body = files.map((f) => ({
        filename: f.name,
        source_type: 'action',
      }));
      const res = await fetch(`${PRESIGNED_URL_ENDPOINT}/api/v1/presignedurl/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new Error(`Presigned URL generation failed: ${res.status} ${res.statusText}`);
      }
      const json = await res.json();
      return json.data as Array<{ presigned_url: string; source_id: string; file_name: string }>;
    };

    /** Step 2: Upload all files to S3 in parallel using their presigned URLs. */
    const uploadFilesToS3 = async (
      files: File[],
      presignedEntries: Array<{ presigned_url: string; source_id: string; file_name: string }>
    ): Promise<void> => {
      await Promise.all(
        files.map((file, idx) => {
          const entry = presignedEntries[idx];
          if (!entry?.presigned_url) return Promise.resolve();
          return fetch(entry.presigned_url, {
            method: 'PUT',
            // No Authorization header — presigned URL is self-authenticating
            headers: { 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
          }).then((res) => {
            if (!res.ok) {
              throw new Error(`S3 upload failed for ${file.name}: ${res.status}`);
            }
          });
        })
      );
    };

    /** Step 3: Call the document parser and return the flat parser_results. */
    const parseDocuments = async (
      files: File[],
      presignedEntries: Array<{ presigned_url: string; source_id: string; file_name: string }>
    ): Promise<Record<string, unknown>> => {
      const sources = presignedEntries.map((entry, idx) => {
        const file = files[idx];
        const ext = (file?.name ?? '').split('.').pop()?.toLowerCase() ?? '';
        return {
          source_id: entry.source_id,
          source_extension: ext,
          file_name: entry.file_name,
        };
      });

      const res = await fetch(`${DOC_PARSER_ENDPOINT}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          sources,
          token,
        }),
      });
      if (!res.ok) {
        throw new Error(`Document parser failed: ${res.status} ${res.statusText}`);
      }
      const json = await res.json();
      // parser_results lives at data[0].parser_results
      return (json?.data?.[0]?.parser_results ?? {}) as Record<string, unknown>;
    };

    /**
     * Full 3-step upload pipeline. Returns flat parser_results keyed by
     * schemaPath / field ID, ready for formPrefillFunction.
     */
    const extractFromDocument = async ({
      files,
    }: {
      files: File[];
    }): Promise<Record<string, unknown>> => {
      if (!files.length) return {};
      const presigned = await generatePresignedUrls(files);
      await uploadFilesToS3(files, presigned);
      return parseDocuments(files, presigned);
    };

    // -----------------------------------------------------------------------
    // 3Cs enrichment endpoint — called whenever complaint / cause / correction
    // changes (debounced 500 ms). The full endpoint URL is set via the
    // CTY_3CS_UPDATE_URL env var. Response shape matches the document parser:
    //   data[0].parser_results → { '3Cs': [...], labor_lines: [...], part_lines: [...] }
    // so mapInvoiceParserToFormData is reused directly.
    // -----------------------------------------------------------------------
    const THREE_CS_UPDATE_URL =
      (import.meta.env.CTY_3CS_UPDATE_URL as string | undefined) ?? '';

    const fetch3CsUpdate = async (
      payload: Record<string, any>
    ): Promise<Record<string, unknown>> => {
      if (!THREE_CS_UPDATE_URL) return {};
      const res = await fetch(THREE_CS_UPDATE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`3Cs enrichment failed: ${res.status}`);
      const json = await res.json();
      return (json?.data?.[0]?.parser_results ?? {}) as Record<string, unknown>;
    };

    /**
     * Safe dot-path accessor with array index support (spec §8.1).
     * traverseDotPath(obj, "labor_lines[0].labor_amount") → "0.00"
     */
    const traverseDotPath = (obj: Record<string, any>, path: string): unknown =>
      path
        .replace(/\[(\d+)\]/g, '.$1')
        .split('.')
        .reduce((cur: any, key: string) => cur?.[key], obj);

    // -----------------------------------------------------------------------
    // Section event forwarder
    // -----------------------------------------------------------------------
    const onSectionEvent = (_payload: unknown) => {
      // Reserved for future event bus / analytics integration
    };

    // -----------------------------------------------------------------------
    // useClaimsIntakeFormRuntime (spec §6)
    //
    // The primary hook FormComponent calls as its very first hook. Owns all
    // form state, validation, repeatable row management, file handling, and
    // auto-fill tracking.
    // -----------------------------------------------------------------------
    const useClaimsIntakeFormRuntime = (
      initialData?: Record<string, any>,
      _options?: Record<string, unknown>
    ) => {
      const [formData, setFormData] = React.useState<Record<string, any>>(
        // Treat an empty object `{}` the same as undefined — fall back to defaults.
        // Generated components call useClaimsIntakeFormRuntime({}) with an empty
        // object, which must not suppress the default initial form data.
        () =>
          initialData && Object.keys(initialData).length > 0
            ? initialData
            : DEFAULT_INITIAL_FORM_DATA
      );
      const [errors, setErrors] = React.useState<Record<string, string>>({});
      const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([]);
      const [fieldLoading, setFieldLoading] = React.useState<Record<string, boolean>>({});
      const [fieldMessages, setFieldMessages] = React.useState<Record<string, string>>({});
      // autoFilledFields: { [sectionKey]: Set<fieldId> } — must be React state,
      // not a plain Set, so that badge display re-renders correctly (spec §6.4).
      const [autoFilledFields, setAutoFilledFields] = React.useState<
        Record<string, Set<string>>
      >({});
      // extracting: boolean while extractFromDocument is running (spec §6.2)
      const [extracting, setExtracting] = React.useState(false);
      const [extractError, setExtractError] = React.useState<string | null>(null);
      // is3CsEnriching: true while the 3Cs enrichment API call is in-flight
      const [is3CsEnriching, setIs3CsEnriching] = React.useState(false);

      // Stores raw parser_results from the last file upload so they can be
      // included in the 3Cs enrichment payload whenever 3Cs change.
      // Plain ref (no re-render needed when it updates).
      const lastParserResultsRef = React.useRef<Record<string, unknown>>({});
      // Stable pointer to the latest formData for use inside debounce callbacks
      // (avoids stale closure over the formData snapshot at debounce-set time).
      const formDataRef = React.useRef(formData);
      const debounce3CsRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

      React.useEffect(() => {
        formDataRef.current = formData;
      }, [formData]);

      React.useEffect(() => {
        return () => {
          if (debounce3CsRef.current) clearTimeout(debounce3CsRef.current);
        };
      }, []);

      // ---- Scalar field setter (spec §6.2) --------------------------------
      // Supports two calling conventions used by AI-generated components:
      //   3-arg: setFieldValue(sectionKey, fieldId, value)   — spec form
      //   2-arg: setFieldValue('section.field', value)       — dot-path shorthand
      const setFieldValue = (
        sectionKeyOrPath: string,
        fieldIdOrValue: unknown,
        maybeValue?: unknown
      ) => {
        let sectionKey: string;
        let fieldId: string;
        let val: unknown;

        if (maybeValue !== undefined) {
          // 3-arg form
          sectionKey = sectionKeyOrPath;
          fieldId = fieldIdOrValue as string;
          val = maybeValue;
        } else {
          // 2-arg dot-path form: 'section.field'
          const dot = sectionKeyOrPath.indexOf('.');
          sectionKey = dot !== -1 ? sectionKeyOrPath.slice(0, dot) : sectionKeyOrPath;
          fieldId = dot !== -1 ? sectionKeyOrPath.slice(dot + 1) : (fieldIdOrValue as string);
          val = dot !== -1 ? fieldIdOrValue : undefined;
        }

        setFormData((prev) => ({
          ...prev,
          [sectionKey]: { ...(prev[sectionKey] ?? {}), [fieldId]: val },
        }));
        setErrors((prev) => {
          const next = { ...prev };
          delete next[`${sectionKey}.${fieldId}`];
          delete next[fieldId];
          return next;
        });
      };

      // ---- Bulk field setter — marks auto-filled (spec §6.4) --------------
      const applyFieldPatch = (
        sectionKey: string,
        patchObject: Record<string, unknown>
      ) => {
        setFormData((prev) => ({
          ...prev,
          [sectionKey]: { ...(prev[sectionKey] ?? {}), ...patchObject },
        }));
        setAutoFilledFields((prev) => ({
          ...prev,
          [sectionKey]: new Set([
            ...(prev[sectionKey] ?? []),
            ...Object.keys(patchObject),
          ]),
        }));
      };

      // ---- Top-level repeatable row handlers (spec §6.2) ------------------
      const THREE_C_FIELDS = new Set(['complaint', 'cause', 'correction']);

      const setRepeatableFieldValue = (
        sectionKey: string,
        rowIndex: number,
        fieldId: string,
        value: unknown
      ) => {
        setFormData((prev) => {
          const rows = [...(prev[sectionKey] ?? [])];
          rows[rowIndex] = { ...(rows[rowIndex] ?? {}), [fieldId]: value };
          return { ...prev, [sectionKey]: rows };
        });

        // When a 3C field changes, debounce an enrichment API call.
        // formDataRef is kept in sync via useEffect so the callback always
        // reads the latest repairs — including this change — after React flushes.
        if (sectionKey === 'repairs' && THREE_C_FIELDS.has(fieldId)) {
          if (debounce3CsRef.current) clearTimeout(debounce3CsRef.current);
          debounce3CsRef.current = setTimeout(async () => {
            // Build payload from the latest committed formData. We apply the
            // changed value manually here because the state update above may
            // not yet be visible in formDataRef at callback time.
            const latestRepairs: Record<string, any>[] = [
              ...(formDataRef.current.repairs ?? []),
            ];
            latestRepairs[rowIndex] = {
              ...(latestRepairs[rowIndex] ?? {}),
              [fieldId]: value,
            };
            const payload = build3CsUpdatePayload(
              latestRepairs,
              lastParserResultsRef.current
            );
            setIs3CsEnriching(true);
            try {
              const result = await fetch3CsUpdate(payload);
              if (result && Object.keys(result).length > 0) {
                const { service_center, repairs } = mapInvoiceParserToFormData(
                  result as InvoiceParserResults
                );
                if (Object.keys(service_center).length > 0) {
                  applyFieldPatch('service_center', service_center);
                }
                if (repairs.length > 0) {
                  setRepeatableRows('repairs', repairs);
                  setAutoFilledFields((prev) => ({
                    ...prev,
                    repairs: new Set([
                      ...(prev.repairs ?? []),
                      'complaint', 'cause', 'correction', 'labor_lines', 'part_lines',
                    ]),
                    service_center: new Set([
                      ...(prev.service_center ?? []),
                      'service_name', 'contact_name', 'physical_address',
                    ]),
                  }));
                }
              }
            } catch {
              // Enrichment is non-blocking — silent failure keeps the form usable
            } finally {
              setIs3CsEnriching(false);
            }
          }, 500);
        }
      };

      const addRepeatableRow = (
        sectionKey: string,
        template?: Record<string, unknown>
      ) =>
        setFormData((prev) => ({
          ...prev,
          [sectionKey]: [
            ...(prev[sectionKey] ?? []),
            template ?? (sectionKey === 'repairs' ? BLANK_REPAIR_ROW() : {}),
          ],
        }));

      const removeRepeatableRow = (sectionKey: string, rowIndex: number) =>
        setFormData((prev) => ({
          ...prev,
          [sectionKey]: (prev[sectionKey] ?? []).filter(
            (_: unknown, i: number) => i !== rowIndex
          ),
        }));

      const setRepeatableRows = (
        sectionKey: string,
        rows: Record<string, unknown>[]
      ) => setFormData((prev) => ({ ...prev, [sectionKey]: rows }));

      // ---- Nested repeatable row handlers (spec §6.2) ---------------------
      // Used for labor_lines / part_lines inside repairs[].

      const setNestedRepeatableField = (
        sectionKey: string,
        rowIndex: number,
        nestedKey: string,
        nestedRowIndex: number,
        fieldId: string,
        value: unknown
      ) =>
        setFormData((prev) => {
          const rows = [...(prev[sectionKey] ?? [])];
          const row = { ...(rows[rowIndex] ?? {}) };
          const nested = [...(row[nestedKey] ?? [])];
          nested[nestedRowIndex] = { ...(nested[nestedRowIndex] ?? {}), [fieldId]: value };
          row[nestedKey] = nested;
          rows[rowIndex] = row;
          return { ...prev, [sectionKey]: rows };
        });

      const addNestedRow = (
        sectionKey: string,
        rowIndex: number,
        nestedKey: string
      ) =>
        setFormData((prev) => {
          const rows = [...(prev[sectionKey] ?? [])];
          const row = { ...(rows[rowIndex] ?? {}) };
          const blankFn = BLANK_NESTED_ROWS[nestedKey];
          row[nestedKey] = [...(row[nestedKey] ?? []), blankFn ? blankFn() : {}];
          rows[rowIndex] = row;
          return { ...prev, [sectionKey]: rows };
        });

      const removeNestedRow = (
        sectionKey: string,
        rowIndex: number,
        nestedKey: string,
        nestedRowIndex: number
      ) =>
        setFormData((prev) => {
          const rows = [...(prev[sectionKey] ?? [])];
          const row = { ...(rows[rowIndex] ?? {}) };
          row[nestedKey] = (row[nestedKey] ?? []).filter(
            (_: unknown, i: number) => i !== nestedRowIndex
          );
          rows[rowIndex] = row;
          return { ...prev, [sectionKey]: rows };
        });

      const setNestedRows = (
        sectionKey: string,
        rowIndex: number,
        nestedKey: string,
        rows: Record<string, unknown>[]
      ) =>
        setFormData((prev) => {
          const sectionRows = [...(prev[sectionKey] ?? [])];
          const row = { ...(sectionRows[rowIndex] ?? {}) };
          row[nestedKey] = rows;
          sectionRows[rowIndex] = row;
          return { ...prev, [sectionKey]: sectionRows };
        });

      // ---- Validation (spec §6.3) ------------------------------------------
      // validateSection: validates one section, updates errors state, and
      // returns the errors map so FormComponent can block navigation.
      const validateSection = (
        sectionKey: string,
        sectionData: Record<string, any>,
        fc: Record<string, any>
      ): Record<string, string> => {
        const nextErrors = runValidateSection(sectionKey, sectionData, fc);
        if (Object.keys(nextErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...nextErrors }));
        }
        return nextErrors;
      };

      // validateForm: validates all non-readOnly sections, returns combined map.
      const validateForm = (
        fd: Record<string, any>,
        fc: Record<string, any>
      ): Record<string, string> => {
        const allErrors: Record<string, string> = {};
        for (const section of fc?.sections ?? []) {
          if (section.readOnly) continue;
          if (section.fieldType === 'repeatable_group') continue;
          Object.assign(allErrors, runValidateSection(section.key, fd?.[section.key] ?? {}, fc));
        }
        if (Object.keys(allErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...allErrors }));
        }
        return allErrors;
      };

      // ---- Async action runner (manages fieldLoading, spec §6.2) ----------
      // Supports two call signatures emitted by AI-generated components:
      //   runAsyncAction(fieldId, asyncFn)  — spec form, tracks loading per field
      //   runAsyncAction(asyncFn)           — 1-arg shorthand, no field tracking
      // Always returns the resolved value so callers can await the result.
      const runAsyncAction = async (
        fieldIdOrFn: string | (() => Promise<unknown>),
        asyncFn?: () => Promise<unknown>
      ): Promise<unknown> => {
        const fn = typeof fieldIdOrFn === 'function' ? fieldIdOrFn : asyncFn!;
        const fieldId = typeof fieldIdOrFn === 'string' ? fieldIdOrFn : null;

        if (fieldId) setFieldLoading((prev) => ({ ...prev, [fieldId]: true }));
        try {
          return await fn();
        } catch {
          return undefined;
        } finally {
          if (fieldId) setFieldLoading((prev) => ({ ...prev, [fieldId]: false }));
        }
      };

      // ---- File upload and extraction prefill (spec §8.2) -----------------
      // Accepts: input change event, drag event, or a raw File array.
      // 1. Adds files to uploadedFiles state immediately (optimistic).
      // 2. Runs the 3-step upload pipeline (presign → S3 → parser).
      // 3. Calls formPrefillFunction with parser_results + formConfig to get
      //    a flat { fieldId: value } map, then applies it section-by-section.
      const handleUploadChange = async (
        eventOrFiles:
          | React.ChangeEvent<HTMLInputElement>
          | React.DragEvent<HTMLElement>
          | Event
          | File[]
      ) => {
        let files: File[];
        if (Array.isArray(eventOrFiles)) {
          files = eventOrFiles;
        } else {
          const fileList =
            (eventOrFiles as React.ChangeEvent<HTMLInputElement>).target?.files ??
            (eventOrFiles as React.DragEvent<HTMLElement>).dataTransfer?.files;
          files = Array.from(fileList ?? []) as File[];
        }
        if (!files.length) return;

        // Optimistically add to the uploaded list so the UI shows filenames
        setUploadedFiles((prev) => [...prev, ...files]);
        setExtracting(true);
        setExtractError(null);

        try {
          // Run the full upload + parse pipeline for all files at once
          const parserResults = await extractFromDocument({ files });
          // Persist raw results so handle3CChange can include them in the
          // enrichment payload even after the upload step has completed.
          lastParserResultsRef.current = parserResults;

          if (Object.keys(parserResults).length > 0) {
            // Use the invoice-specific mapper which understands the parser's
            // domain keys (3Cs, invoice_generator_name, labor_lines, etc.)
            // and builds the correct nested structure for service_center and repairs.
            const { service_center, repairs } = mapInvoiceParserToFormData(
              parserResults as InvoiceParserResults
            );

            if (Object.keys(service_center).length > 0) {
              applyFieldPatch('service_center', service_center);
            }

            if (repairs.length > 0) {
              setRepeatableRows('repairs', repairs);
              // Mark the prefilled fields as auto-filled for badge display
              setAutoFilledFields((prev) => ({
                ...prev,
                repairs: new Set([
                  ...(prev.repairs ?? []),
                  'complaint', 'cause', 'correction', 'labor_lines', 'part_lines',
                ]),
                service_center: new Set([
                  ...(prev.service_center ?? []),
                  'service_name', 'contact_name', 'physical_address',
                ]),
              }));
            }
          }
        } catch (err) {
          setExtractError(
            err instanceof Error ? err.message : 'Document extraction failed.'
          );
        } finally {
          setExtracting(false);
        }
      };

      const removeUploadedFile = (fileOrIndex: File | number) =>
        setUploadedFiles((prev) =>
          typeof fileOrIndex === 'number'
            ? prev.filter((_, i) => i !== fileOrIndex)
            : prev.filter((f) => f !== fileOrIndex)
        );

      // ---- 3Cs change handler — triggers enrichment API -------------------
      // Drop-in replacement for setRepeatableFieldValue when editing complaint,
      // cause, or correction. Updates local state immediately, then debounces
      // a POST to the enrichment endpoint 500 ms after the last change.
      //
      // The response is in the same shape as the document parser so
      // mapInvoiceParserToFormData is reused — prefill runs identically
      // whether the data came from a file upload or the enrichment API.
      //
      // ---- Utility helpers (spec §6.2) ------------------------------------
      const fireSectionEvent = (eventName: string, sectionData: unknown) =>
        onSectionEvent({ event: eventName, data: sectionData });

      const getFieldError = (fieldId: string): string | undefined =>
        errors[fieldId];

      // Returns the formData slice for a section key (spec §6.2)
      const getSectionFormData = (sectionKey: string) =>
        formData[sectionKey] ?? {};

      // Returns true if the field was populated via applyFieldPatch (spec §6.2)
      const isAutoFilled = (sectionKey: string, fieldId: string): boolean =>
        autoFilledFields[sectionKey]?.has(fieldId) ?? false;

      // Set a transient success/info message on a field (used by FormComponent
      // after fetchContractDetails to show "✓ Contract retrieved — {number}")
      const setFieldMessage = (fieldId: string, message: string) =>
        setFieldMessages((prev) => ({ ...prev, [fieldId]: message }));

      // Convenience wrappers — generated components destructure these by name.
      // These are no-ops on the host side; actual submission is handled via submitClaim.
      const submitCurrentForm = () => { /* handled by submitClaim host function */ };
      const saveCurrentForm = () => { /* handled by saveRepairsAndDocs host function */ };

      // Returns a badge label if a field was auto-filled, empty string otherwise.
      // Called as: getAutoFieldBadge(sectionKey, fieldId) or getAutoFieldBadge(dotPath)
      const getAutoFieldBadge = (
        sectionKeyOrPath: string,
        fieldId?: string
      ): string => {
        if (fieldId !== undefined) {
          return autoFilledFields[sectionKeyOrPath]?.has(fieldId) ? 'Auto-filled' : '';
        }
        // dot-path form: "contract_details.contract_number"
        const [sec, fid] = sectionKeyOrPath.split('.');
        return autoFilledFields[sec]?.has(fid) ? 'Auto-filled' : '';
      };

      // Returns the complete formData for the review step.
      //
      // The AI-generated review component can call this instead of reading
      // individual section slices — guaranteeing that repeatable groups
      // (repairs[], labor_lines[], part_lines[]) and all scalar sections are
      // included, exactly as the user filled them in.
      //
      // Optionally accepts a section key to return only that slice.
      const getFormDataForReview = (sectionKey?: string): Record<string, any> => {
        if (sectionKey) return formData[sectionKey] ?? {};
        return { ...formData };
      };

      // ---------------------------------------------------------------------------
      // renderClaimReview(fd?)
      //
      // Host-side util that renders a complete, read-only review of all form data.
      // Uses React.createElement so it works inside a plain .ts file.
      //
      // Call signature (server component):
      //   const { renderClaimReview } = useClaimsIntakeFormRuntime();
      //   // In review step JSX:
      //   return <div>{renderClaimReview()}</div>;
      //   // Or pass explicit snapshot:
      //   return <div>{renderClaimReview(formData)}</div>;
      //
      // Renders:
      //   - Contract & Vehicle  (contract_details + vehicle_coverage)
      //   - Vehicle Details     (vehicle_details)
      //   - Reimbursement       (claim_details)
      //   - Service Center      (service_center)
      //   - Repairs[]           — each card includes complaint/cause/correction,
      //                           labor_lines[] table, part_lines[] table, subtotal
      //   - Financial Summary   (computed from repairs[] totals)
      // ---------------------------------------------------------------------------
      const renderClaimReview = (fd?: Record<string, any>): React.ReactElement => {
        const data = fd ?? formData;
        const ce = React.createElement;
        const fmt = (n: number) => `$${n.toFixed(2)}`;

        // ── helpers ────────────────────────────────────────────────────────────
        const reviewField = (label: string, value: unknown, key: string) =>
          ce('div', { key, className: 'flex flex-col gap-1' },
            ce('span', { className: 'text-[10px] text-gray-400 uppercase font-semibold tracking-wide' }, label),
            ce('span', { className: 'text-sm text-gray-800 font-medium' },
              (value !== undefined && value !== null && String(value) !== '') ? String(value) : '—'
            )
          );

        const sectionCard = (title: string, children: React.ReactNode, key: string) =>
          ce('div', { key, className: 'bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-4' },
            ce('h3', { className: 'text-xs font-bold text-gray-400 uppercase tracking-widest mb-4' }, title),
            children
          );

        const grid = (...fields: React.ReactElement[]) =>
          ce('div', { className: 'grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4' }, ...fields);

        const miniTable = (headers: string[], rows: React.ReactElement[]) =>
          ce('div', { className: 'overflow-x-auto rounded-lg border border-gray-100' },
            ce('table', { className: 'w-full text-xs text-left' },
              ce('thead', { className: 'bg-gray-50' },
                ce('tr', null,
                  ...headers.map((h, i) =>
                    ce('th', { key: i, className: 'px-3 py-2 text-gray-500 font-semibold uppercase' }, h)
                  )
                )
              ),
              ce('tbody', { className: 'divide-y divide-gray-100' }, ...rows)
            )
          );

        // ── data slices ────────────────────────────────────────────────────────
        const cd: Record<string, any> = data.contract_details ?? {};
        const vc: Record<string, any> = data.vehicle_coverage ?? {};
        const vd: Record<string, any> = data.vehicle_details ?? {};
        const cl: Record<string, any> = data.claim_details ?? {};
        const sc: Record<string, any> = data.service_center ?? {};
        const repairs: Record<string, any>[] = data.repairs ?? [];

        // ── totals ─────────────────────────────────────────────────────────────
        const repairTotals = (r: Record<string, any>) => {
          const labor = (r.labor_lines ?? []).reduce(
            (s: number, l: any) => s + (parseFloat(l.labor_amount) || 0), 0
          );
          const parts = (r.part_lines ?? []).reduce(
            (s: number, p: any) => s + (parseFloat(p.part_amount) || 0), 0
          );
          return { labor, parts, total: labor + parts };
        };
        const grand = repairs.reduce(
          (acc, r) => {
            const t = repairTotals(r);
            return { labor: acc.labor + t.labor, parts: acc.parts + t.parts, total: acc.total + t.total };
          },
          { labor: 0, parts: 0, total: 0 }
        );
        const deductible = parseFloat(cd.deductible) || 0;

        // ── repair cards ───────────────────────────────────────────────────────
        const repairCards = repairs.map((repair, idx) => {
          const t = repairTotals(repair);

          const laborRows: React.ReactElement[] = (repair.labor_lines ?? []).length > 0
            ? (repair.labor_lines ?? []).map((l: any, li: number) =>
              ce('tr', { key: li },
                ce('td', { className: 'px-3 py-2 text-gray-700' }, l.labor_description || '—'),
                ce('td', { className: 'px-3 py-2 text-gray-500' }, l.labor_repair_notes || '—'),
                ce('td', { className: 'px-3 py-2 font-semibold text-gray-800' },
                  fmt(parseFloat(l.labor_amount) || 0)
                )
              )
            )
            : [ce('tr', { key: 'empty' },
              ce('td', { colSpan: 3, className: 'px-3 py-2 text-gray-400 italic' }, 'No labor lines')
            )];

          const partRows: React.ReactElement[] = (repair.part_lines ?? []).length > 0
            ? (repair.part_lines ?? []).map((p: any, pi: number) =>
              ce('tr', { key: pi },
                ce('td', { className: 'px-3 py-2 font-mono text-[11px] text-gray-700' }, p.part_number || '—'),
                ce('td', { className: 'px-3 py-2 text-gray-700' }, p.part_description || '—'),
                ce('td', { className: 'px-3 py-2 text-center' }, String(p.part_quantity ?? '0')),
                ce('td', { className: 'px-3 py-2' },
                  p.part_unit_price ? fmt(parseFloat(p.part_unit_price)) : '—'
                ),
                ce('td', { className: 'px-3 py-2 font-semibold text-gray-800' },
                  p.part_amount ? fmt(parseFloat(p.part_amount)) : '—'
                )
              )
            )
            : [ce('tr', { key: 'empty' },
              ce('td', { colSpan: 5, className: 'px-3 py-2 text-gray-400 italic' }, 'No parts')
            )];

          return ce('div', { key: idx, className: 'bg-white rounded-xl border border-gray-100 shadow-sm p-6 mb-3' },
            ce('p', { className: 'text-sm font-bold text-gray-800 mb-4' }, `Repair #${idx + 1}`),
            // 3Cs
            ce('div', { className: 'grid grid-cols-1 gap-3 mb-5' },
              ce('div', { key: 'c' },
                ce('p', { className: 'text-[10px] text-gray-400 font-bold uppercase mb-1' }, 'Complaint'),
                ce('p', { className: 'text-sm text-gray-700 leading-relaxed' }, repair.complaint || '—')
              ),
              ce('div', { key: 'ca' },
                ce('p', { className: 'text-[10px] text-gray-400 font-bold uppercase mb-1' }, 'Cause'),
                ce('p', { className: 'text-sm text-gray-700 leading-relaxed' }, repair.cause || '—')
              ),
              ce('div', { key: 'co' },
                ce('p', { className: 'text-[10px] text-gray-400 font-bold uppercase mb-1' }, 'Correction'),
                ce('p', { className: 'text-sm text-gray-700 leading-relaxed' }, repair.correction || '—')
              )
            ),
            // Labor lines table
            ce('div', { className: 'mb-4' },
              ce('p', { className: 'text-[10px] font-bold text-gray-500 uppercase mb-2' }, 'Labor Lines'),
              miniTable(['Description', 'Notes', 'Labor ($)'], laborRows)
            ),
            // Part lines table
            ce('div', { className: 'mb-4' },
              ce('p', { className: 'text-[10px] font-bold text-gray-500 uppercase mb-2' }, 'Parts'),
              miniTable(['Part #', 'Description', 'Qty', 'Unit $', 'Amount $'], partRows)
            ),
            // Row subtotal
            ce('div', { className: 'flex justify-end gap-6 pt-3 border-t border-gray-100 text-xs text-gray-600' },
              ce('span', null, 'Labor: ', ce('strong', { className: 'text-gray-900' }, fmt(t.labor))),
              ce('span', null, 'Parts: ', ce('strong', { className: 'text-gray-900' }, fmt(t.parts))),
              ce('span', { className: 'font-bold text-blue-700 uppercase tracking-wide' },
                `Subtotal: ${fmt(t.total)}`
              )
            )
          );
        });

        // ── financial summary ──────────────────────────────────────────────────
        const financialCard = ce('div', {
          key: 'financial',
          className: 'bg-blue-900 rounded-xl p-6 text-white mt-2',
        },
          ce('h3', { className: 'text-xs font-bold uppercase tracking-widest text-blue-300 mb-4' },
            'Financial Summary'
          ),
          ce('div', { className: 'grid grid-cols-2 md:grid-cols-4 gap-4' },
            ce('div', { key: 'l' },
              ce('p', { className: 'text-[10px] uppercase text-blue-300 mb-1' }, 'Total Labor'),
              ce('p', { className: 'text-lg font-bold' }, fmt(grand.labor))
            ),
            ce('div', { key: 'p' },
              ce('p', { className: 'text-[10px] uppercase text-blue-300 mb-1' }, 'Total Parts'),
              ce('p', { className: 'text-lg font-bold' }, fmt(grand.parts))
            ),
            ce('div', { key: 'd' },
              ce('p', { className: 'text-[10px] uppercase text-blue-300 mb-1' }, 'Deductible'),
              ce('p', { className: 'text-lg font-bold' }, `-${fmt(deductible)}`)
            ),
            ce('div', { key: 'tc', className: 'border-l border-blue-700 pl-4' },
              ce('p', { className: 'text-[10px] uppercase text-blue-300 mb-1' }, 'Total Claim'),
              ce('p', { className: 'text-2xl font-black' }, fmt(Math.max(0, grand.total - deductible)))
            )
          )
        );

        // ── assemble ───────────────────────────────────────────────────────────
        return ce(React.Fragment, null,
          sectionCard('Contract & Vehicle', grid(
            reviewField('Contract #', cd.contract_number, 'cn'),
            reviewField('Holder', cd.contract_holder, 'ch'),
            reviewField('Coverage Plan', cd.coverage_plan, 'cp'),
            reviewField('Vehicle',
              [vc.model_year, vc.make, vc.model_trim].filter(Boolean).join(' ') || '—',
              'veh'
            ),
            reviewField('VIN', vc.vin_full || cd.vin, 'vin'),
            reviewField('Dealer Code', cd.dealer_code, 'dc'),
            reviewField('Effective Date', cd.effective_date, 'ed'),
            reviewField('Expiry Date', cd.expiration_date, 'exp'),
            reviewField('Deductible', cd.deductible != null ? fmt(parseFloat(cd.deductible)) : '—', 'ded'),
            reviewField('Max Coverage', cd.max_coverage != null ? fmt(parseFloat(cd.max_coverage)) : '—', 'mc'),
          ), 'contract'),
          sectionCard('Vehicle Context', grid(
            reviewField('Arrival Method', vd.arrival_method, 'am'),
            reviewField('At Service Center', vd.at_service_center, 'asc'),
          ), 'vehicle'),
          sectionCard('Reimbursement', grid(
            reviewField('Reimbursement Method', cl.reimbursement_method, 'rm'),
          ), 'claim'),
          sectionCard('Service Center', grid(
            reviewField('Service Name', sc.service_name, 'sn'),
            reviewField('Contact Name', sc.contact_name, 'con'),
            reviewField('Contact Phone', sc.contact_phone, 'cp'),
            reviewField('Contact Email', sc.contact_email, 'ce'),
            reviewField('Physical Address', sc.physical_address, 'pa'),
            reviewField('Billing Address', sc.billing_address, 'ba'),
            reviewField('Labor Rate Schedule', sc.labor_rate_schedule, 'lrs'),
            reviewField('Labor Rate', sc.labor_rate != null ? fmt(parseFloat(sc.labor_rate)) : '—', 'lr'),
          ), 'sc'),
          ce('h3', {
            key: 'repairs-title',
            className: 'text-xs font-bold text-gray-400 uppercase tracking-widest mb-3',
          }, 'Repairs'),
          ...repairCards,
          financialCard
        );
      };

      return {
        // State
        formData,
        errors,
        uploadedFiles,
        fieldLoading,
        fieldMessages,
        autoFilledFields,
        extracting,
        extractError,
        is3CsEnriching,
        // Scalar handler
        setFieldValue,
        // Bulk / auto-fill handler
        applyFieldPatch,
        // Top-level repeatable row handlers
        setRepeatableFieldValue,
        addRepeatableRow,
        removeRepeatableRow,
        setRepeatableRows,
        // Nested repeatable row handlers
        setNestedRepeatableField,
        addNestedRow,
        removeNestedRow,
        setNestedRows,
        // Validation
        validateSection,
        validateForm,
        // Async / loading
        runAsyncAction,
        // File upload
        handleUploadChange,
        removeUploadedFile,
        // Utilities
        fireSectionEvent,
        getFieldError,
        getSectionFormData,
        isAutoFilled,
        getAutoFieldBadge,
        setFieldMessage,
        getFormDataForReview,
        renderClaimReview,
        // Convenience aliases used by some generated components
        submitCurrentForm,
        saveCurrentForm,
        // ---- Alternate names the AI sometimes generates ----------------------
        // setGroupFieldValue is an alias for setFieldValue (3-arg form).
        setGroupFieldValue: setFieldValue,
        // updateFieldValue is another common alias the AI emits.
        updateFieldValue: setFieldValue,
        // setFormField is another alias seen in some generated outputs.
        setFormField: setFieldValue,
        // clearFieldError removes a specific error key.
        clearFieldError: (fieldId: string) =>
          setErrors((prev) => {
            const next = { ...prev };
            delete next[fieldId];
            return next;
          }),
        // resetSection clears all field values for a section.
        resetSection: (sectionKey: string) =>
          setFormData((prev) => ({ ...prev, [sectionKey]: {} })),
      };
    };

    // -----------------------------------------------------------------------
    // Public host functions — injected into FormComponent scope (spec §4.4)
    // -----------------------------------------------------------------------
    return {
      // ---- Required scope members (spec §4.4) ----------------------------
      useClaimsIntakeFormRuntime,

      // Step 1: Lookup Contract button handler (spec §7.1)
      fetchContractDetails: async ({ vin }: { vin: string }) => {
        const raw = await fetchJson(
          import.meta.env.CTY_API_URL + '/api/v3/integrations',
          {
            method: 'POST',
            body: JSON.stringify({
              source_id: '2b49a3e7-d021-497c-a022-c8aa994c60d3',
              payload: { query_parameters: { vin } },
            }),
          }
        );
        const contract = raw?.data?.[0]?.contracts?.[0];
        if (!contract) throw new Error('No contract found for this VIN.');
        _lastFetchedContract = contract;
        return normalizeContractDetails(contract);
      },

      // Step 2 entry effect (spec §7.2)
      fetchVehicleAndClaims: async ({
        contract_number: _cn,
        vin: _vin,
      }: {
        contract_number: string;
        vin: string;
      }) => {
        if (_lastFetchedContract) {
          return normalizeVehicleCoverage(_lastFetchedContract);
        }
        throw new Error(
          'No contract data available. Please look up the contract first.'
        );
      },

      // Step 3 Continue-to-Review handler (spec §7.3)
      saveRepairsAndDocs: async (
        _payload: Record<string, unknown>
      ): Promise<{ saved: boolean; draft_id: string }> => ({
        saved: true,
        draft_id: `draft_${Date.now()}`,
      }),

      // Step 4 entry effect (spec §7.4) — review is client-side only, no API needed
      generateReviewSummary: async (_payload: Record<string, unknown>) => ({
        success: true,
      }),

      // Step 5 Submit Claim button handler (spec §7.5)
      submitClaim: async ({
        form_data,
        files: _files,
      }: {
        form_data: Record<string, unknown>;
        files: File[];
      }) =>
        fetchJson('/api/claims/submit', {
          method: 'POST',
          body: JSON.stringify({ form_data }),
        }),

      // AI Assist rewrite (spec §7.6)
      rewriteNarrative: async ({
        text,
        context,
      }: {
        text: string;
        context: string;
      }) =>
        fetchJson('/api/ai/rewrite', {
          method: 'POST',
          body: JSON.stringify({ text, context }),
        }),

      // ---- Additional utilities available to component scope --------------
      traverseDotPath,

      // Fallback host values — AI-generated components sometimes reference
      // these directly without calling useClaimsIntakeFormRuntime and would
      // otherwise throw ReferenceError at render time.
      formData: {} as Record<string, any>,
      errors: {} as Record<string, string>,
      uploadedFiles: [] as File[],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
};
